/**
 * @fileoverview Task list / table view (MOR-696 Stage 2)
 *
 * Presentational table for the task set, the list-mode sibling of the kanban
 * board. Receives the already-filtered `CalendarEventRow[]` plus the firm
 * status / member / label lookups, sorts by a clickable column header, and
 * optionally partitions rows into collapsible group sections (by status /
 * assignee / priority / label / job). Row click opens the shared side panel.
 *
 * Filtering lives upstream in `applyTaskFilters` so this view stays a pure
 * renderer shared by the job board route and the workspace "My tasks" inbox.
 */

import { Fragment, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CalendarClock,
  Repeat,
} from "lucide-react";

import { type CalendarEventRow, isEventKind } from "@/calendar/eventAdapter";
import {
  AutomationChip,
  type RowAutomation,
} from "@/calendar/components/automation-chip";
import { typeBadgeMeta } from "@/calendar/categoryMeta";
import { priorityMeta } from "@/calendar/task-priority";
import type {
  TaskViewGroupBy,
  TaskViewSortBy,
} from "@cvx/tasks/taskViews/schema";
import type { BoardMember } from "@/calendar/components/board/task-board-card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { Checkbox } from "@/ui/primitives/checkbox";
import { UserAvatar, getDisplayName } from "@/ui/composed";
import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { TASK_COLOR_DOT } from "@/components/settings/tasks/task-colors";
import { cn } from "@/utils/misc";
import { TASK_LIST_COLUMNS } from "./task-list-view.helpers";

export interface TaskListLookups {
  statusLookup: Map<
    string,
    { name: string; color: BadgeColor; sortOrder: number }
  >;
  memberById: Map<string, BoardMember>;
  labelLookup: Map<string, { name: string; color: BadgeColor }>;
}

export type SortDir = "asc" | "desc";

function shortDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return format(parseISO(iso), "d MMM yyyy");
  } catch {
    return iso;
  }
}

function memberName(member: BoardMember | undefined): string {
  if (!member) return "Unassigned";
  return getDisplayName(member.firstName, member.lastName, member.email);
}

function compareBy(
  a: CalendarEventRow,
  b: CalendarEventRow,
  sortBy: TaskViewSortBy,
  dir: SortDir,
): number {
  const mul = dir === "asc" ? 1 : -1;
  switch (sortBy) {
    case "title":
      return a.title.localeCompare(b.title) * mul;
    case "priority":
      // Priority 0 ("none") sorts last in ascending order.
      return ((a.priority || 99) - (b.priority || 99) || 0) * mul;
    case "plannedDate":
      return (a.plannedDate ?? "").localeCompare(b.plannedDate ?? "") * mul;
    case "manual":
      // Manual ordering uses the kanban boardRank; rows without one trail.
      if (a.boardRank && b.boardRank) {
        return (a.boardRank < b.boardRank ? -1 : 1) * mul;
      }
      if (a.boardRank) return -1;
      if (b.boardRank) return 1;
      return a.date.localeCompare(b.date) * mul;
    case "created":
    case "date":
      // No `_creationTime` on the wire row; "created" falls back to due date.
      return a.date.localeCompare(b.date) * mul;
    default: {
      const exhaustive: never = sortBy;
      return exhaustive;
    }
  }
}

/** Columns that map to a `TaskViewSortBy` value and so are click-sortable. */
const SORTABLE: Partial<Record<string, TaskViewSortBy>> = {
  title: "title",
  priority: "priority",
  plannedDate: "plannedDate",
  date: "date",
};

/** Sentinel key for rows with no value under the active grouping. */
const NO_GROUP_KEY = "__none__";

/**
 * Order two group sections. Status groups follow the configured status
 * `sortOrder` (matching the board's column order); every other grouping falls
 * back to alphabetical by label. In all cases the "none" bucket (No status /
 * Unassigned / No label) sinks to the bottom.
 */
function compareGroups(
  a: { key: string; label: string },
  b: { key: string; label: string },
  groupBy: TaskViewGroupBy,
  lookups: TaskListLookups,
): number {
  const aNone = a.key === NO_GROUP_KEY;
  const bNone = b.key === NO_GROUP_KEY;
  if (aNone !== bNone) return aNone ? 1 : -1;
  if (groupBy === "status") {
    const ao =
      lookups.statusLookup.get(a.key)?.sortOrder ?? Number.MAX_SAFE_INTEGER;
    const bo =
      lookups.statusLookup.get(b.key)?.sortOrder ?? Number.MAX_SAFE_INTEGER;
    if (ao !== bo) return ao - bo;
  }
  return a.label.localeCompare(b.label);
}

/** Group key + display label for a row under the active grouping. */
function groupFor(
  row: CalendarEventRow,
  groupBy: TaskViewGroupBy,
  lookups: TaskListLookups,
): { key: string; label: string } {
  switch (groupBy) {
    case "status":
      return row.statusId
        ? {
            key: String(row.statusId),
            label:
              lookups.statusLookup.get(String(row.statusId))?.name ?? "Unknown",
          }
        : { key: "__none__", label: "No status" };
    case "assignee":
      return row.assigneeUserId
        ? {
            key: String(row.assigneeUserId),
            label: memberName(
              lookups.memberById.get(String(row.assigneeUserId)),
            ),
          }
        : { key: "__none__", label: "Unassigned" };
    case "priority": {
      const meta = priorityMeta(row.priority);
      return { key: String(meta.value), label: meta.label };
    }
    case "label": {
      const first = row.labelIds[0];
      return first
        ? {
            key: String(first),
            label: lookups.labelLookup.get(String(first))?.name ?? "Label",
          }
        : { key: "__none__", label: "No label" };
    }
    case "job":
      return { key: String(row.jobId), label: row.jobName };
    case "none":
      return { key: "__all__", label: "" };
    default: {
      const exhaustive: never = groupBy;
      return exhaustive;
    }
  }
}

export function TaskListView({
  events,
  lookups,
  groupBy = "none",
  sortBy = "date",
  sortDir = "asc",
  onSortChange,
  onRowClick,
  visibleColumns,
  crossJob = false,
  selectedIds,
  onToggleSelect,
  onSetSelection,
  automationsByRow,
}: {
  events: CalendarEventRow[];
  lookups: TaskListLookups;
  groupBy?: TaskViewGroupBy;
  sortBy?: TaskViewSortBy;
  sortDir?: SortDir;
  /** When provided, column headers become click-sortable. */
  onSortChange?: (sortBy: TaskViewSortBy, dir: SortDir) => void;
  onRowClick: (event: CalendarEventRow) => void;
  /** Column keys to show; undefined ⇒ the default-visible set (MOR-707). */
  visibleColumns?: ReadonlySet<string>;
  /** Cross-job workspace view — enables the Job column. */
  crossJob?: boolean;
  /** When set (with onToggleSelect), render a selection checkbox column. */
  selectedIds?: ReadonlySet<string>;
  onToggleSelect?: (row: CalendarEventRow) => void;
  /** Header checkbox: replace the whole selection with these row ids. */
  onSetSelection?: (ids: string[]) => void;
  /** Automation rules targeting each row, keyed by String(row.id). */
  automationsByRow?: ReadonlyMap<string, RowAutomation[]>;
}) {
  // Internal sort state used when the host doesn't control sorting.
  const [localSort, setLocalSort] = useState<{
    sortBy: TaskViewSortBy;
    dir: SortDir;
  }>({ sortBy, dir: sortDir });
  const activeSortBy = onSortChange ? sortBy : localSort.sortBy;
  const activeDir = onSortChange ? sortDir : localSort.dir;

  const toggleSort = (key: TaskViewSortBy) => {
    const nextDir: SortDir =
      activeSortBy === key && activeDir === "asc" ? "desc" : "asc";
    if (onSortChange) onSortChange(key, nextDir);
    else setLocalSort({ sortBy: key, dir: nextDir });
  };

  const groups = useMemo(() => {
    const sorted = [...events].sort((a, b) =>
      compareBy(a, b, activeSortBy, activeDir),
    );
    if (groupBy === "none") {
      return [{ key: "__all__", label: "", rows: sorted }];
    }
    const map = new Map<
      string,
      { key: string; label: string; rows: CalendarEventRow[] }
    >();
    for (const row of sorted) {
      const g = groupFor(row, groupBy, lookups);
      const bucket = map.get(g.key) ?? { ...g, rows: [] };
      bucket.rows.push(row);
      map.set(g.key, bucket);
    }
    return [...map.values()].sort((a, b) =>
      compareGroups(a, b, groupBy, lookups),
    );
  }, [events, activeSortBy, activeDir, groupBy, lookups]);

  const columns = useMemo(
    () =>
      TASK_LIST_COLUMNS.filter((c) => {
        // The Job column only applies to the cross-job workspace view.
        if (c.crossJobOnly && !crossJob) return false;
        return visibleColumns ? visibleColumns.has(c.key) : c.defaultVisible;
      }),
    [visibleColumns, crossJob],
  );

  const selectable = !!onToggleSelect;
  const colSpan = columns.length + (selectable ? 1 : 0);
  const allSelected =
    selectable &&
    events.length > 0 &&
    events.every((e) => selectedIds?.has(String(e.id)));

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {selectable && (
              <TableHead className="w-10">
                <Checkbox
                  checked={allSelected}
                  aria-label="Select all tasks"
                  onCheckedChange={(checked) =>
                    onSetSelection?.(
                      checked ? events.map((e) => String(e.id)) : [],
                    )
                  }
                  className="size-4"
                />
              </TableHead>
            )}
            {columns.map((col) => {
              const mapped = SORTABLE[col.key];
              if (!mapped) {
                return (
                  <TableHead key={col.key} className={col.className}>
                    {col.label}
                  </TableHead>
                );
              }
              const active = activeSortBy === mapped;
              return (
                <TableHead key={col.key} className={col.className}>
                  <button
                    type="button"
                    onClick={() => toggleSort(mapped)}
                    className="flex items-center gap-1.5 text-left transition-colors hover:text-foreground"
                  >
                    {col.label}
                    {active ? (
                      activeDir === "asc" ? (
                        <ArrowUp className="size-3.5" />
                      ) : (
                        <ArrowDown className="size-3.5" />
                      )
                    ) : (
                      <ArrowUpDown className="size-3.5 text-muted-foreground/50" />
                    )}
                  </button>
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={colSpan}
                className="h-24 text-center text-muted-foreground"
              >
                No tasks match the current filters.
              </TableCell>
            </TableRow>
          ) : (
            groups.map((group) => (
              <Fragment key={group.key}>
                {groupBy !== "none" && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell
                      colSpan={colSpan}
                      className="bg-muted/50 py-1.5 text-xs font-semibold text-muted-foreground"
                    >
                      {group.label}
                      <span className="ml-2 font-normal tabular-nums">
                        {group.rows.length}
                      </span>
                    </TableCell>
                  </TableRow>
                )}
                {group.rows.map((row) => (
                  <TaskRow
                    key={row.id}
                    row={row}
                    lookups={lookups}
                    columns={columns}
                    selected={selectedIds?.has(String(row.id)) ?? false}
                    automations={automationsByRow?.get(String(row.id))}
                    onToggleSelect={
                      selectable ? () => onToggleSelect?.(row) : undefined
                    }
                    onClick={() => onRowClick(row)}
                  />
                ))}
              </Fragment>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

/** Render a member (avatar + name) or a muted dash. */
function MemberCell({ member }: { member: BoardMember | undefined }) {
  if (!member) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex items-center gap-1.5">
      <UserAvatar
        avatarUrl={member.avatarUrl}
        firstName={member.firstName}
        lastName={member.lastName}
        email={member.email}
        className="size-5 text-[9px]"
        fallbackClassName="text-[9px]"
      />
      <span className="text-sm">{memberName(member)}</span>
    </div>
  );
}

function TaskRow({
  row,
  lookups,
  columns,
  selected,
  automations,
  onToggleSelect,
  onClick,
}: {
  row: CalendarEventRow;
  lookups: TaskListLookups;
  columns: Array<{ key: string }>;
  selected: boolean;
  automations?: RowAutomation[];
  onToggleSelect?: () => void;
  onClick: () => void;
}) {
  const status = row.statusId
    ? lookups.statusLookup.get(String(row.statusId))
    : undefined;
  const member = row.assigneeUserId
    ? lookups.memberById.get(String(row.assigneeUserId))
    : undefined;
  const assignor = row.assignorUserId
    ? lookups.memberById.get(String(row.assignorUserId))
    : undefined;
  const priority = priorityMeta(row.priority);
  const PriorityIcon = priority.icon;
  const showPriority = !!row.priority && row.priority > 0;
  const labels = row.labelIds
    .map((id) => lookups.labelLookup.get(String(id)))
    .filter((l): l is { name: string; color: BadgeColor } => !!l);
  const categoryMeta = typeBadgeMeta(
    isEventKind(row),
    row.category,
    Boolean(row.parentTaskId),
  );
  const CategoryIcon = categoryMeta.icon;

  const cells: Record<string, React.ReactNode> = {
    title: (
      <div className="flex items-center gap-1.5">
        {/* Category icon in its category colour (MOR-707). */}
        <span title={categoryMeta.label}>
          <CategoryIcon
            className={cn("size-3.5 shrink-0", categoryMeta.iconFgClass)}
          />
        </span>
        <span className="font-medium">{row.title}</span>
        {row.recurrenceRuleId && (
          <Repeat
            className="size-3 shrink-0 text-muted-foreground"
            aria-label="Recurring task"
          />
        )}
        {row.subtaskTotal > 0 && (
          <span className="text-xs tabular-nums text-muted-foreground">
            {row.subtaskCompleted}/{row.subtaskTotal}
          </span>
        )}
        {automations && automations.length > 0 && (
          <AutomationChip automations={automations} />
        )}
      </div>
    ),
    job: (
      <span className="text-sm text-muted-foreground">
        {row.jobName || "—"}
      </span>
    ),
    status: status ? (
      <Badge color={status.color} variant="soft">
        {status.name}
      </Badge>
    ) : (
      <span className="text-muted-foreground">—</span>
    ),
    assignee: <MemberCell member={member} />,
    assignor: <MemberCell member={assignor} />,
    priority: showPriority ? (
      <span className="inline-flex items-center gap-1 text-sm">
        <span
          className={cn("size-2 rounded-full", TASK_COLOR_DOT[priority.color])}
        />
        <PriorityIcon className="size-3.5" />
        {priority.label}
      </span>
    ) : (
      <span className="text-muted-foreground">—</span>
    ),
    category: (
      <span className="inline-flex items-center gap-1.5 text-sm">
        <CategoryIcon className={cn("size-3.5", categoryMeta.iconFgClass)} />
        {categoryMeta.label}
      </span>
    ),
    plannedDate: (
      <span className="text-sm text-muted-foreground">
        {shortDate(row.plannedDate)}
      </span>
    ),
    date: (
      <span
        className={cn(
          "inline-flex items-center gap-1 text-sm",
          row.overdue && row.category !== "appointment"
            ? "font-medium text-red-600 dark:text-red-400"
            : "text-muted-foreground",
        )}
      >
        <CalendarClock className="size-3.5" />
        {shortDate(row.date)}
      </span>
    ),
    labels:
      labels.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {labels.map((l) => (
            <Badge
              key={l.name}
              color={l.color}
              variant="soft"
              className="px-1 py-0 text-[10px]"
            >
              {l.name}
            </Badge>
          ))}
        </div>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  };

  return (
    <TableRow className="cursor-pointer" onClick={onClick}>
      {onToggleSelect && (
        <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={selected}
            aria-label={`Select ${row.title}`}
            onCheckedChange={() => onToggleSelect()}
            className="size-4"
          />
        </TableCell>
      )}
      {columns.map((col) => (
        <TableCell key={col.key}>{cells[col.key] ?? null}</TableCell>
      ))}
    </TableRow>
  );
}
